import { LegalPageShell, LegalSection, LegalP, LegalList, LegalSubhead } from "./LegalShell";

export default function PrivacyPage() {
  return (
    <LegalPageShell
      badge="Legal"
      title="Privacy"
      titleEm="Policy"
      lastUpdated="14 June 2026"
    >
      <LegalSection id="intro" num="00" title="About this policy">
        <LegalP>
          JiraniApp ("we", "us", "our") is committed to protecting your personal information. This
          Privacy Policy explains what data we collect, how we use it, who we share it with, and the
          rights you have over your information when you use the JiraniApp platform, including our
          website and mobile applications (together, the "Platform").
        </LegalP>
        <LegalP>
          By creating an account or using JiraniApp, you acknowledge that you have read and understood
          this policy. If you do not agree with how we handle your data, please do not use the Platform.
        </LegalP>
      </LegalSection>

      <LegalSection id="collection" num="01" title="Information we collect">
        <LegalSubhead>Information you provide</LegalSubhead>
        <LegalList items={[
          "Account details such as your name, phone number, and optional email address when you register.",
          "Profile information including your location, profile photo, and any bio or service descriptions you add.",
          "Booking details including service type, address, scheduling preferences, and any special instructions.",
          "Payment information such as M-Pesa transaction references. We do not store full card numbers or mobile money PINs.",
          "Communications you send through the Platform, including messages to JiraniPros and support requests.",
          "Reviews, ratings, and other content you submit.",
        ]} />
        <LegalSubhead>Information collected automatically</LegalSubhead>
        <LegalList items={[
          "Device and browser information such as your IP address, device model, operating system, and app version.",
          "Usage data including pages viewed, features used, time spent on the Platform, and interactions.",
          "Location data if you grant the Platform permission to access your device location, used to match you with nearby JiraniPros.",
          "Log data including crash reports, error logs, and diagnostic information that help us identify and fix issues.",
        ]} />
        <LegalSubhead>Information from third parties</LegalSubhead>
        <LegalList items={[
          "Identity verification data from third-party providers used to verify JiraniPro credentials.",
          "Payment confirmation data from M-Pesa or other supported payment processors.",
        ]} />
      </LegalSection>

      <LegalSection id="use" num="02" title="How we use your information">
        <LegalP>
          We use the information we collect to operate, improve, and personalise JiraniApp. Specifically,
          we use your data to:
        </LegalP>
        <LegalList items={[
          "Create and manage your account and keep it secure.",
          "Match you with relevant JiraniPros based on location, availability, and service type.",
          "Process bookings and payments and send booking confirmations and reminders.",
          "Provide customer support and resolve disputes through our Trust and Safety team.",
          "Send platform notifications including service updates, booking status changes, and important policy communications.",
          "Improve the Platform by analysing usage patterns and identifying areas for development.",
          "Enforce our Terms of Service and take action against fraud, abuse, or unsafe activity.",
          "Comply with legal obligations under Kenyan law and other applicable regulations.",
        ]} />
        <LegalP>
          We will not use your data to send unsolicited marketing communications without your consent,
          and you can opt out of optional communications at any time through your account settings.
        </LegalP>
      </LegalSection>

      <LegalSection id="cookies" num="03" title="Cookies and similar technologies">
        <LegalP>
          JiraniApp uses cookies and similar tracking technologies on our web platform to support
          authentication, remember your preferences, and understand how users interact with the Platform.
        </LegalP>
        <LegalSubhead>Types of cookies we use</LegalSubhead>
        <LegalList items={[
          "Essential cookies that are necessary for the Platform to function, including session and authentication cookies. These cannot be disabled.",
          "Functional cookies that remember your preferences such as language and location settings.",
          "Analytics cookies that help us understand how users navigate the Platform so we can improve it. We use anonymised or aggregated data for this purpose.",
        ]} />
        <LegalP>
          You can control cookies through your browser settings. Note that disabling certain cookies may
          affect functionality. Our mobile applications use equivalent local storage and device
          identifiers rather than browser cookies.
        </LegalP>
      </LegalSection>

      <LegalSection id="sharing" num="04" title="How we share your information">
        <LegalP>
          We do not sell your personal information. We share data only in the limited circumstances
          described below:
        </LegalP>
        <LegalSubhead>With JiraniPros</LegalSubhead>
        <LegalP>
          When you make a booking, relevant details including your name, service address, and booking
          instructions are shared with the JiraniPro assigned to your job. JiraniPros are bound by our
          Terms of Service and are prohibited from using your information outside of fulfilling bookings.
        </LegalP>
        <LegalSubhead>With service providers</LegalSubhead>
        <LegalP>
          We share data with trusted third-party providers who help us operate the Platform, including
          cloud hosting, SMS and notification delivery, payment processing, and identity verification.
          These providers are contractually required to handle your data securely and only for the
          purposes we specify.
        </LegalP>
        <LegalSubhead>For legal and safety reasons</LegalSubhead>
        <LegalP>
          We may disclose your information if required by law, court order, or government authority, or
          where we believe disclosure is necessary to prevent harm, fraud, or a serious threat to the
          safety of any person.
        </LegalP>
        <LegalSubhead>Business transfers</LegalSubhead>
        <LegalP>
          If JiraniApp merges with or is acquired by another company, your information may be transferred
          as part of that transaction. We will notify you before your data becomes subject to a different
          privacy policy.
        </LegalP>
      </LegalSection>

      <LegalSection id="retention" num="05" title="Data retention">
        <LegalP>
          We retain your personal data for as long as your account is active and for a reasonable period
          afterward to fulfil the purposes described in this policy, resolve disputes, and comply with
          legal obligations.
        </LegalP>
        <LegalList items={[
          "Account data is retained for the lifetime of your account and for up to 2 years after account closure.",
          "Booking records and transaction data may be retained for up to 7 years in accordance with Kenyan tax and financial regulations.",
          "Support communications are retained for up to 3 years.",
          "You may request deletion of your data at any time, subject to our legal retention obligations.",
        ]} />
      </LegalSection>

      <LegalSection id="rights" num="06" title="Your rights">
        <LegalP>
          Under the Kenya Data Protection Act, 2019, you have the following rights regarding your
          personal information:
        </LegalP>
        <LegalList items={[
          "Right of access: you may request a copy of the personal data we hold about you.",
          "Right to rectification: you may request that we correct inaccurate or incomplete data.",
          "Right to erasure: you may request deletion of your data where we no longer have a lawful basis to retain it.",
          "Right to object: you may object to our processing of your data for direct marketing or where processing is based on legitimate interests.",
          "Right to data portability: you may request a copy of your data in a structured, machine-readable format.",
          "Right to withdraw consent: where processing is based on consent, you may withdraw it at any time without affecting the lawfulness of prior processing.",
        ]} />
        <LegalP>
          To exercise any of these rights, please contact us at{" "}
          <a className="jrn-legal-link" href="mailto:privacy@jiraniapp.com">privacy@jiraniapp.com</a>.
          We will respond within 30 days. We may need to verify your identity before processing your
          request.
        </LegalP>
      </LegalSection>

      <LegalSection id="security" num="07" title="Security">
        <LegalP>
          We implement industry-standard technical and organisational measures to protect your personal
          information against unauthorised access, loss, or misuse. These include encrypted data
          transmission (TLS), access controls, and regular security reviews.
        </LegalP>
        <LegalP>
          No system is completely secure. If you believe your account has been compromised, please
          contact us immediately at{" "}
          <a className="jrn-legal-link" href="mailto:security@jiraniapp.com">security@jiraniapp.com</a>.
        </LegalP>
      </LegalSection>

      <LegalSection id="minors" num="08" title="Children's privacy">
        <LegalP>
          JiraniApp is not directed at or intended for use by anyone under the age of 18. We do not
          knowingly collect personal data from minors. If we become aware that a user under 18 has
          created an account, we will take steps to remove their information and close the account
          promptly.
        </LegalP>
      </LegalSection>

      <LegalSection id="changes" num="09" title="Changes to this policy">
        <LegalP>
          We may update this Privacy Policy from time to time to reflect changes in our practices, legal
          requirements, or the features of the Platform. We will notify you of material changes through
          the Platform or via SMS where appropriate. The updated policy will take effect on the date
          shown at the top of this page.
        </LegalP>
        <LegalP>
          We encourage you to review this policy periodically to stay informed about how we protect your
          information.
        </LegalP>
      </LegalSection>

      <LegalSection id="contact" num="10" title="Contact us">
        <LegalP>
          If you have questions about this Privacy Policy or how we handle your data, please contact our
          team at{" "}
          <a className="jrn-legal-link" href="mailto:privacy@jiraniapp.com">privacy@jiraniapp.com</a> or
          through the <a className="jrn-legal-link" href="/help">Help &amp; Support</a> page. You may
          also write to us at:
        </LegalP>
        <LegalP>
          JiraniApp Data Privacy Team<br />
          Nairobi, Kenya
        </LegalP>
      </LegalSection>
    </LegalPageShell>
  );
}